import { ChartConfigEntry } from '../context/GoogleSheetsContext';
import { SERVER_BASE_URL } from '../config';

/**
 * Client for the Postgres chart-data API (migration off Google Sheets).
 * Slice 1: discovery only (chart config + weekly-sheet list). Returns the SAME
 * shapes the components already consume, so callers swap with minimal change.
 * Gated by USE_CHART_DB (config) at the call sites; this service just talks to
 * the API. Auth is attached by the global installAuthFetch window.fetch patch.
 */

export interface ChartDataFile {
  id: string;   // = the chart_week's source_sheet_id (the existing spreadsheetId key)
  name: string; // e.g. "6-13-26"
}

/** A chart-style row WITH its DB id, for the Chart Settings editor. */
export interface ChartStyle {
  id: number;
  name: string;
  horizontalTemplateId: string;
  verticalTemplateId: string;
  voSubfolder: string;
  chartId: string;
  defaultOutroText: string;
  themeColor: string;
  bilingual: boolean;
}

/** A Master Video List entry (the `videos` table). */
export interface Video {
  id: number;
  song: string;
  artist: string;
  s3Url: string;
  youtubeLink: string;
  inPoint: string;
  duration: string;
  zoom: string;
  verticalUrl: string;
  seekIn: string;
}

/** A chart position (1..10) for a week, with its linked video's framing fields. */
export interface ChartPosition {
  position: number;
  song: string;
  artist: string;
  lastWeek: string;
  peak: string;
  weeks: string;
  message: string;
  videoId: number | null;
  s3Url: string;
  inPoint: string;
  duration: string;
  zoom: string;
  verticalUrl: string;
  youtubeLink: string;
  seekIn: string;
}

/** A render-history (outputs log) entry. */
export interface RenderHistoryEntry {
  id: number;
  datetime: string;
  chart: string;
  chartDate: string;
  orientation: string;
  s3Link: string;
  viewed: string;
  language: string;
}

export const chartDataService = {
  /** Chart config map (name -> ChartConfigEntry) + category names. */
  async getCharts(): Promise<{ chartConfig: { [name: string]: ChartConfigEntry }; categories: string[] }> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/charts`);
    if (!r.ok) throw new Error(`chart-data/charts ${r.status}`);
    return r.json();
  },

  /** Full chart-style rows (with ids) for the Chart Settings page. */
  async listCharts(): Promise<ChartStyle[]> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/charts/list`);
    if (!r.ok) throw new Error(`chart-data/charts/list ${r.status}`);
    return (await r.json()).charts || [];
  },

  /** Create a new chart style. Returns the created row. */
  async createChart(fields: Omit<ChartStyle, 'id'>): Promise<ChartStyle> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/charts`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
    });
    if (!r.ok) { let m = `chart-data/charts POST ${r.status}`; try { m = (await r.json()).error || m; } catch { /* */ } throw new Error(m); }
    return (await r.json()).chart;
  },

  /** Update an existing chart style (name is not editable). Returns the row. */
  async updateChart(id: number, fields: Partial<Omit<ChartStyle, 'id' | 'name'>>): Promise<ChartStyle> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/charts/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
    });
    if (!r.ok) { let m = `chart-data/charts PUT ${r.status}`; try { m = (await r.json()).error || m; } catch { /* */ } throw new Error(m); }
    return (await r.json()).chart;
  },

  /** Delete a chart style. Throws (409) if the chart still has weeks. */
  async deleteChart(id: number): Promise<void> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/charts/${id}`, { method: 'DELETE' });
    if (!r.ok) { let m = `chart-data/charts DELETE ${r.status}`; try { m = (await r.json()).error || m; } catch { /* */ } throw new Error(m); }
  },

  /** Weekly entries for a chart: { id (source key), name } per week. */
  async listWeeks(chartName: string): Promise<ChartDataFile[]> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/weeks?chart=${encodeURIComponent(chartName)}`);
    if (!r.ok) throw new Error(`chart-data/weeks ${r.status}`);
    const d = await r.json();
    return d.files || [];
  },

  /**
   * VO data for a week as the keyed maps VOManager.fetchVOData builds.
   * Keys: '10'..'1' | 'Intro' | 'Outro' | 'Tease'.
   */
  async getVO(sheetId: string, language: string = 'en'): Promise<{
    sheetName: string;
    dateString: string | null;
    globalGain: string | null;
    processedVoUrl: string | null;
    voData: { [k: string]: string };
    voInPoints: { [k: string]: string };
    voOutPoints: { [k: string]: string };
    lineVoUrls: { [k: string]: string };
    uploadedVoUrls: { [k: string]: string };
    lineGain: { [k: string]: number };
    songTitles: { [k: string]: string };
    songArtists: { [k: string]: string };
  }> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/vo?sheetId=${encodeURIComponent(sheetId)}&language=${encodeURIComponent(language)}`);
    if (!r.ok) throw new Error(`chart-data/vo ${r.status}`);
    return r.json();
  },

  /**
   * Full weekly chart table reconstructed from the DB as { values } (header row +
   * 10 position rows, 22 cols) — the shape SpreadsheetViewer's grid renders.
   * `language` selects which VO
   * language fills the VO columns (positions/videos/settings are shared); defaults
   * to 'en' so single-language charts are unchanged.
   */
  async getWeek(sheetId: string, language: string = 'en'): Promise<{ values: string[][] }> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/week?sheetId=${encodeURIComponent(sheetId)}&language=${encodeURIComponent(language)}`);
    if (!r.ok) throw new Error(`chart-data/week ${r.status}`);
    return r.json();
  },

  // (weekly writes use the typed setPositionField / setVoField / setSetting
  //  methods below — no A1 notation, no Google Sheets.)

  /**
   * Create a new weekly "sheet" in the DB (carries over prev week's settings +
   * positions, no VO). Returns { id, name } where id is the synthetic
   * source_sheet_id — used exactly like a Drive spreadsheetId downstream.
   */
  async createWeek(chartName: string, sheetName: string, dateString?: string): Promise<ChartDataFile> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/weeks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ chart: chartName, sheetName, dateString }),
    });
    if (!r.ok) {
      let msg = `chart-data/weeks POST ${r.status}`;
      try { msg = (await r.json()).error || msg; } catch { /* ignore */ }
      throw new Error(msg);
    }
    const d = await r.json();
    return { id: d.id, name: d.name };
  },

  // ---- Typed, Sheets-free domain methods (no A1 notation anywhere) ----

  /** Master Video List — the whole list. */
  async listVideos(): Promise<Video[]> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/videos`);
    if (!r.ok) throw new Error(`chart-data/videos ${r.status}`);
    return (await r.json()).videos || [];
  },
  /** Add a video (upserts on song+artist). */
  async createVideo(fields: Partial<Omit<Video, 'id'>> & { song: string; artist: string }): Promise<Video> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/videos`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
    });
    if (!r.ok) { let m = `videos POST ${r.status}`; try { m = (await r.json()).error || m; } catch { /* */ } throw new Error(m); }
    return (await r.json()).video;
  },
  /** Update whitelisted fields of one video by id. */
  async updateVideo(id: number, fields: Partial<Omit<Video, 'id'>>): Promise<Video> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/videos/${id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fields),
    });
    if (!r.ok) { let m = `videos PUT ${r.status}`; try { m = (await r.json()).error || m; } catch { /* */ } throw new Error(m); }
    return (await r.json()).video;
  },
  /** Update/insert a video by (song, artist) — for import/upload/autocrop flows. */
  async updateVideoByKey(song: string, artist: string, fields: Partial<Omit<Video, 'id' | 'song' | 'artist'>>): Promise<Video | null> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/videos/by-key`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ song, artist, ...fields }),
    });
    if (!r.ok) { let m = `videos/by-key ${r.status}`; try { m = (await r.json()).error || m; } catch { /* */ } throw new Error(m); }
    return (await r.json()).video;
  },
  /** Delete a video by id. */
  async deleteVideo(id: number): Promise<number> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/videos/${id}`, { method: 'DELETE' });
    if (!r.ok) throw new Error(`videos DELETE ${r.status}`);
    return (await r.json()).deleted;
  },

  /** Render history (outputs log). */
  async listRenderHistory(): Promise<RenderHistoryEntry[]> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/render-history`);
    if (!r.ok) throw new Error(`render-history ${r.status}`);
    return (await r.json()).entries || [];
  },
  async addRenderHistory(entry: Partial<RenderHistoryEntry>): Promise<number> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/render-history`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(entry),
    });
    if (!r.ok) throw new Error(`render-history POST ${r.status}`);
    return (await r.json()).id;
  },

  /** Chart positions 1..10 for a week (with linked video framing fields). */
  async getPositions(sheetId: string): Promise<ChartPosition[]> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/positions?sheetId=${encodeURIComponent(sheetId)}`);
    if (!r.ok) throw new Error(`positions ${r.status}`);
    return (await r.json()).positions || [];
  },
  /** Set one field of a position (position fields) or its linked video (framing fields). */
  async setPositionField(sheetId: string, position: number, field: string, value: string): Promise<void> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/position`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sheetId, position, field, value }),
    });
    if (!r.ok) { let m = `position ${r.status}`; try { m = (await r.json()).error || m; } catch { /* */ } throw new Error(m); }
  },

  /**
   * Set many positions' text fields in ONE atomic request (server writes all fields,
   * then re-links each position's video). Used by reorder + chart import to replace
   * N+1 setPositionField calls. `positions[].fields` may include any of
   * song/artist/lastWeek/peak/weeks/message.
   */
  async setPositionsBulk(
    sheetId: string,
    positions: Array<{ position: number; fields: Record<string, string> }>,
  ): Promise<void> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/positions/bulk`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sheetId, positions }),
    });
    if (!r.ok) { let m = `positions/bulk ${r.status}`; try { m = (await r.json()).error || m; } catch { /* */ } throw new Error(m); }
  },

  /** Set one VO field for a section (position_N | intro | outro | tease). */
  async setVoField(sheetId: string, section: string, field: string, value: string, language: string = 'en'): Promise<void> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/vo-field`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sheetId, section, field, value, language }),
    });
    if (!r.ok) { let m = `vo-field ${r.status}`; try { m = (await r.json()).error || m; } catch { /* */ } throw new Error(m); }
  },

  /** Settings (graphics/intro/outro/tease) as key→value, plus week-level fields. */
  async getSettings(sheetId: string): Promise<{ settings: { [k: string]: any }; globalGain: string | null; processedVoUrl: string | null; dateString: string | null }> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/settings?sheetId=${encodeURIComponent(sheetId)}`);
    if (!r.ok) throw new Error(`settings ${r.status}`);
    return r.json();
  },
  /** Set one settings key, or a week-level field key: 'globalGain' | 'processedVoUrl' | 'dateString'. */
  async setSetting(sheetId: string, key: string, value: any): Promise<void> {
    const r = await fetch(`${SERVER_BASE_URL}/api/chart-data/setting`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sheetId, key, value }),
    });
    if (!r.ok) { let m = `setting ${r.status}`; try { m = (await r.json()).error || m; } catch { /* */ } throw new Error(m); }
  },
};
